import { Component, Inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Subject } from 'rxjs';
import { BaseService } from '../../../services/base.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { OtpForm } from './otp.interface';
import { decodeBase64, trimValidator } from '../../../helpers';
import { AuthService } from '../../../services/auth.service';
import { ActivatedRoute, Router } from '@angular/router';
import { IError } from '../../../interfaces';
import { DEFAULT_ERROR, ERROR_MESSAGES, activeTranslations } from '../../../enum/constants';
import { NotificationService } from '../../../services/notifications.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { environment } from '../../../../environments/environment';
import { KeycloakService } from 'keycloak-angular';

@Component({
  selector: 'app-otp',
  templateUrl: './otp.component.html',
  styleUrls: ['./otp.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class OtpComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<null>();

  public otpForm!: FormGroup<OtpForm>;
  public phone: string = '';
  public email: string = '';
  public password: string = '';

  isSending = false;
  resendCooldown = 0;
  private cooldownSec = 60;
  private timer?: any;

  constructor(
    private readonly router: Router,
    public readonly baseService: BaseService,
    private readonly authService: AuthService,
    private route: ActivatedRoute,
    private readonly notificationService: NotificationService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly keycloakService: KeycloakService,
    public dialogRef: MatDialogRef<OtpComponent>,
  ) {
    this.email = data.data.email;
    this.password = data.data.password;
  }

  public ngOnInit() {
    this.initForm();
    this.sendCode();
  }

  public ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
    this.dialogRef.close();
  }

  public initForm() {
    this.otpForm = new FormGroup({
      otp: new FormControl(null, [Validators.required, trimValidator, Validators.minLength(6)]),
    });
  }

  public sendCode() {
    if (this.resendCooldown > 0 || this.isSending) return;
    this.otpForm.reset();
    this.isSending = true;
    this.authService.sendVerifyPhone(this.email).subscribe((res: any) => {
      if (res) {
        this.isSending = false;
        this.startCooldown();
      }
    });
  }

  public onSubmit() {
    this.otpForm.markAllAsTouched();

    if (this.otpForm.invalid) {
      return;
    }

    const body = {
      otp: this.otpForm.controls.otp.value,
      email: this.email,
      password: this.password,
    };

    this.authService.sendVerifyOtp(body).subscribe({
      next: async (res: any) => {
        this.dialogRef.close();

        const token = res.data.access_token;
        const refreshToken = res.data.refresh_token;
        const keycloakOptions = {
          config: { ...environment.keycloak },
          initOptions: {
            checkLoginIframe: false,
            token: token,
            refreshToken: refreshToken,
          },
        };
        try {
          await this.keycloakService.init(keycloakOptions);

          localStorage.setItem('token', token);
          localStorage.setItem('refreshToken', refreshToken);
          if (this.baseService.getCompany()) {
            const redirectCode = this.route.snapshot.queryParamMap.get('redirect_code');
            if (redirectCode) {
              const redirectUrl = decodeBase64(redirectCode);

              if (redirectUrl) window.location.href = redirectUrl;
              else this.router.navigate(['/crm']);
            }
          } else {
            this.router.navigate(['/companies']);
          }
        } catch {
          this.router.navigate(['/login']);
        }
      },
      error: (e: any) => {
        (e.error?.errors || []).forEach((err: IError) => {
          this.notificationService.showError(
            activeTranslations.notifications_texts.error.error,
            ERROR_MESSAGES[err.code] || ERROR_MESSAGES[DEFAULT_ERROR],
          );
        });
      },
    });
  }

  private startCooldown() {
    this.resendCooldown = this.cooldownSec;
    clearInterval(this.timer);
    this.timer = setInterval(() => {
      this.resendCooldown--;
      if (this.resendCooldown <= 0) clearInterval(this.timer);
    }, 1000);
  }
}
